import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Wrench, Save, Plus, Trash2, Upload, X, Check, CheckCheck, Navigation } from "lucide-react";
import { api, photoUrl, uploadFiles, formatApiError } from "../api";
import { SERVICE_TYPES, TOW_STATUS, fmtDate, fmtKm, fmtTry } from "../lib/labels";
import BusinessExtras from "../components/BusinessExtras";
import QuoteInbox from "../components/QuoteInbox";

const inputCls = "w-full px-4 py-2.5 rounded-xl bg-slate-950/70 border border-white/10 text-slate-100 text-sm focus:outline-none focus:border-cyan-400/60 transition-colors";
const labelCls = "block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5";

const EMPTY_RECORD = { vin: "", date: new Date().toISOString().slice(0, 10), title: "", description: "", km: "", cost: "", photos: [] };
const SERVICE_RECORD_TYPES = ["Yağ bakımı", "Cam filmi", "PPF", "Detailing", "Lastik hizmeti", "Mekanik bakım", "Ekspertiz", "Diğer hizmet"];

export default function ServisPanel() {
  const [profile, setProfile] = useState(null);
  const [records, setRecords] = useState([]);
  const [inbox, setInbox] = useState({ gelen: [], aktif: [], tamamlanan: [] });
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [rec, setRec] = useState({ ...EMPTY_RECORD });
  const [vinInfo, setVinInfo] = useState(null);
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const PROFILE_DEFAULTS = { business_name: "", phone: "", city: "", address: "", service_types: [], description: "",
    working_hours: "", available: true, lat: 0, lng: 0, district: "", regions: [], working_days: [],
    is_247: false, whatsapp: "", website: "", logo: "", photos: [], tax_number: "", tax_document: "",
    authorized_person: "", status: "bekliyor", verified: false, business_type: "" };

  const load = () => {
    api.get("/servis/profile").then((r) => setProfile({ ...PROFILE_DEFAULTS, ...r.data }))
      .catch(() => setProfile({ ...PROFILE_DEFAULTS }));
    api.get("/isletme-turleri").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/servis/records").then((r) => setRecords(r.data)).catch(() => setRecords([]));
    api.get("/servis/talep-listesi").then((r) => setInbox(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const svcOptions = (catalog.find((c) => c.key === profile?.business_type)?.services) || SERVICE_TYPES;
  const typeLabel = catalog.find((c) => c.key === profile?.business_type)?.label || "";

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/servis/profile", profile);
      toast.success("Servis profili kaydedildi");
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const addRecord = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/servis/records", {
        ...rec, km: rec.km === "" ? null : Number(rec.km), cost: rec.cost === "" ? null : Number(rec.cost),
      });
      toast.success("Servis kaydı aracın geçmişine eklendi");
      setRec({ ...EMPTY_RECORD });
      setVinInfo(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setAdding(false);
  };

  const lookupVin = async () => {
    setVinInfo(null);
    if (!rec.vin.trim()) { toast.error("Önce şasi numarası girin"); return; }
    try {
      const { data } = await api.get(`/servis/arac-bul/${encodeURIComponent(rec.vin.trim())}`);
      setVinInfo(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeRecord = async (rid) => {
    if (!window.confirm("Bu servis kaydı silinsin mi?")) return;
    try {
      await api.delete(`/servis/records/${rid}`);
      toast.success("Kayıt silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const act = async (tid, action) => {
    try {
      await api.post(`/servis/talep/${tid}/${action}`);
      toast.success(action === "kabul" ? "Talep kabul edildi" : "İş tamamlandı");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleType = (t) => setProfile((p) => ({
    ...p,
    service_types: p.service_types.includes(t) ? p.service_types.filter((x) => x !== t) : [...p.service_types, t],
  }));

  if (profile === null) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="w-10 h-10 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="servis-panel" className="min-h-screen pt-20 sm:pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex items-center gap-3">
          <Wrench className="w-6 h-6 text-cyan-300" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">İşletme Paneli</h1>
        </div>

        {profile.status && profile.status !== "onayli" && (
          <div data-testid="servis-status-banner" className={`rounded-xl border px-4 py-3 text-sm ${
            profile.status === "bekliyor" ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
            : "border-red-500/30 bg-red-500/10 text-red-300"}`}>
            {profile.status === "bekliyor"
              ? "Başvurunuz yönetici doğrulaması bekliyor. Onaylandığında talep almaya başlarsınız."
              : profile.status === "reddedildi"
                ? "Başvurunuz reddedildi. Bilgilerinizi güncelleyip tekrar değerlendirme talep edebilirsiniz."
                : "Hesabınız pasif durumda. Talep almak için yöneticiyle iletişime geçin."}
          </div>
        )}

        {/* Profil */}
        <form onSubmit={saveProfile} data-testid="servis-profile-card"
          className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7 space-y-4">
          <h2 className="text-lg font-semibold text-slate-100">Servis Profili & İletişim</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>İşletme Adı *</label>
              <input data-testid="servis-business-name" required value={profile.business_name}
                onChange={(e) => setProfile({ ...profile, business_name: e.target.value })} className={inputCls} placeholder="Örnek Oto Servis" /></div>
            <div><label className={labelCls}>Telefon</label>
              <input data-testid="servis-phone" value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className={inputCls} placeholder="0 5xx xxx xx xx" /></div>
            <div><label className={labelCls}>Şehir</label>
              <input data-testid="servis-city" value={profile.city}
                onChange={(e) => setProfile({ ...profile, city: e.target.value })} className={inputCls} placeholder="İstanbul" /></div>
            <div><label className={labelCls}>Adres</label>
              <input data-testid="servis-address" value={profile.address}
                onChange={(e) => setProfile({ ...profile, address: e.target.value })} className={inputCls} placeholder="Sanayi Sitesi ..." /></div>
          </div>
          <div>
            <label className={labelCls}>Verilen Hizmetler</label>
            <div className="flex flex-wrap gap-2">
              {svcOptions.map((t) => (
                <button key={t} type="button" data-testid={`servis-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => toggleType(t)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    profile.service_types.includes(t)
                      ? "border-cyan-400/60 bg-cyan-400/15 text-cyan-300"
                      : "border-white/10 text-slate-400 hover:text-slate-200"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div><label className={labelCls}>Açıklama / Tanıtım Yazısı</label>
            <textarea data-testid="servis-description" rows={2} value={profile.description}
              onChange={(e) => setProfile({ ...profile, description: e.target.value })} className={inputCls} /></div>
          <div className="flex items-end pb-1 gap-3">
            <label data-testid="servis-available-toggle" className="flex items-center gap-2.5 cursor-pointer">
              <span className="text-sm text-slate-300">Müsaitim / Hizmet Veriyorum</span>
              <button type="button" onClick={() => setProfile((p) => ({ ...p, available: !p.available }))}
                className={`w-12 h-6 rounded-full transition-colors relative ${profile.available ? "bg-emerald-500" : "bg-slate-700"}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${profile.available ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
            </label>
            <button type="button" data-testid="servis-use-location"
              onClick={() => {
                if (!navigator.geolocation) { toast.error("Tarayıcınız konum desteklemiyor."); return; }
                navigator.geolocation.getCurrentPosition(
                  (p) => { setProfile((pr) => ({ ...pr, lat: p.coords.latitude, lng: p.coords.longitude })); toast.success("Konum alındı"); },
                  () => toast.error("Konum izni verilmedi."), { timeout: 10000 });
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-cyan-400/40 text-cyan-300 text-xs font-semibold hover:bg-cyan-400/10 transition-colors">
              <Navigation className="w-3.5 h-3.5" /> {profile.lat ? "Konum ✓" : "Konumumu Al"}
            </button>
          </div>
          <BusinessExtras profile={profile} setProfile={setProfile} typeLabel={typeLabel} />
          <button data-testid="servis-profile-save" type="submit" disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-cyan-400 text-slate-950 font-bold text-sm hover:bg-cyan-300 transition-all disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "Kaydediliyor..." : "Profili Kaydet"}
          </button>
        </form>

        {/* VIN ile kayıt ekle */}
        <form onSubmit={addRecord} data-testid="servis-record-card"
          className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7 space-y-4">
          <h2 className="text-lg font-semibold text-slate-100">VIN / Şasi ile Servis Kaydı Ekle</h2>
          <p className="text-xs text-slate-500">Kayıt, aracın OTO KÜNYE geçmişine işlenir ve araç raporunda görünür.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className={labelCls}>Şasi No / VIN *</label>
              <div className="flex gap-2">
                <input data-testid="servis-record-vin" required value={rec.vin}
                  onChange={(e) => { setRec({ ...rec, vin: e.target.value.toUpperCase() }); setVinInfo(null); }}
                  className={`${inputCls} font-mono-vin tracking-widest flex-1`} placeholder="17 haneli şasi numarası" />
                <button type="button" data-testid="servis-record-lookup" onClick={lookupVin}
                  className="px-4 py-2.5 rounded-xl border border-cyan-400/40 text-cyan-300 text-sm font-semibold hover:bg-cyan-400/10 transition-colors shrink-0">
                  Aracı Bul
                </button>
              </div>
              {vinInfo && (
                <p data-testid="servis-record-vehicle-info" className="mt-2 text-xs font-semibold text-emerald-300">
                  {vinInfo.brand} {vinInfo.model}{vinInfo.year ? ` • ${vinInfo.year}` : ""}{vinInfo.body_type ? ` • ${vinInfo.body_type}` : ""}
                </p>
              )}</div>
            <div><label className={labelCls}>Tarih *</label>
              <input data-testid="servis-record-date" required type="date" value={rec.date}
                onChange={(e) => setRec({ ...rec, date: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Yapılan İşlem *</label>
              <select data-testid="servis-record-title" required value={rec.title}
                onChange={(e) => setRec({ ...rec, title: e.target.value })} className={inputCls}>
                <option value="">İşlem seçin...</option>
                {SERVICE_RECORD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label className={labelCls}>Kilometre</label>
              <input data-testid="servis-record-km" type="number" min="0" value={rec.km}
                onChange={(e) => setRec({ ...rec, km: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Tutar (₺)</label>
              <input data-testid="servis-record-cost" type="number" min="0" value={rec.cost}
                onChange={(e) => setRec({ ...rec, cost: e.target.value })} className={inputCls} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Açıklama</label>
              <textarea data-testid="servis-record-description" rows={2} value={rec.description}
                onChange={(e) => setRec({ ...rec, description: e.target.value })} className={inputCls} /></div>
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
              onChange={async (e) => {
                setUploading(true);
                try {
                  const paths = await uploadFiles([...e.target.files]);
                  setRec((r) => ({ ...r, photos: [...r.photos, ...paths] }));
                } catch (err) { toast.error(formatApiError(err)); }
                setUploading(false);
                e.target.value = "";
              }} />
            <button type="button" data-testid="servis-record-upload" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-white/20 text-slate-300 text-sm hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
              <Upload className="w-4 h-4" /> {uploading ? "Yükleniyor..." : "Fatura / Fotoğraf Ekle"}
            </button>
            {rec.photos.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {rec.photos.map((p, i) => (
                  <div key={p} className="relative">
                    <img src={photoUrl(p)} alt="" className="w-16 h-12 object-cover rounded-lg border border-white/10" />
                    <button type="button" onClick={() => setRec((r) => ({ ...r, photos: r.photos.filter((_, j) => j !== i) }))}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button data-testid="servis-record-submit" type="submit" disabled={adding || uploading}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-emerald-500 text-slate-950 font-bold text-sm hover:bg-emerald-400 transition-all disabled:opacity-50">
            <Plus className="w-4 h-4" /> {adding ? "Ekleniyor..." : "Kaydı Araca İşle"}
          </button>
        </form>

        {/* Servis Talepleri */}
        <div data-testid="servis-inbox" className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Gelen Servis Talepleri ({inbox.gelen.length})</h2>
          {inbox.gelen.length === 0 ? (
            <p className="text-sm text-slate-500">Bekleyen talep yok.</p>
          ) : (
            <div className="space-y-3">
              {inbox.gelen.map((r) => (
                <div key={r.id} data-testid={`svc-inbox-${r.id}`} className="rounded-xl border border-white/10 bg-slate-950/50 px-4 py-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm text-slate-100">{r.category || "Genel Servis"} — {[r.brand, r.model].filter(Boolean).join(" ") || "Araç"}{r.plate && ` (${r.plate})`}</p>
                      <p className="text-xs text-slate-500 mt-1">{fmtDate(r.created_at)} • {r.user_name || r.user_email}{r.address && ` • ${r.address}`}</p>
                      {r.description && <p className="text-xs text-slate-400 mt-1">{r.description}</p>}
                      {r.photos && r.photos.length > 0 && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {r.photos.map((p) => <img key={p} src={photoUrl(p)} alt="" className="w-14 h-10 object-cover rounded-lg border border-white/10" />)}
                        </div>
                      )}
                    </div>
                    <button data-testid={`svc-accept-${r.id}`} onClick={() => act(r.id, "kabul")}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs font-bold hover:bg-emerald-500/20 transition-colors shrink-0">
                      <Check className="w-3.5 h-3.5" /> Kabul Et
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {inbox.aktif.length > 0 && (
            <>
              <h2 className="text-lg font-semibold text-slate-100 mt-7 mb-4">Aktif İşler ({inbox.aktif.length})</h2>
              <div className="space-y-3">
                {inbox.aktif.map((r) => (
                  <div key={r.id} className="rounded-xl border border-cyan-500/20 bg-slate-950/50 px-4 py-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm text-slate-100">{r.category || "Genel Servis"} — {[r.brand, r.model].filter(Boolean).join(" ") || "Araç"}</p>
                      <p className="text-xs text-slate-500 mt-1">{r.user_name || r.user_email}</p>
                    </div>
                    <button data-testid={`svc-complete-${r.id}`} onClick={() => act(r.id, "tamamla")}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-400/10 border border-cyan-400/40 text-cyan-300 text-xs font-bold hover:bg-cyan-400/20 transition-colors shrink-0">
                      <CheckCheck className="w-3.5 h-3.5" /> İşi Tamamla
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          {inbox.tamamlanan.length > 0 && (
            <>
              <h2 className="text-lg font-semibold text-slate-100 mt-7 mb-4">Tamamlanan İşler ({inbox.tamamlanan.length})</h2>
              <div className="space-y-2">
                {inbox.tamamlanan.map((r) => {
                  const st = TOW_STATUS.tamamlandi;
                  return (
                    <div key={r.id} className="rounded-xl border border-white/10 bg-slate-950/50 px-4 py-3 flex items-center justify-between gap-3">
                      <p className="text-sm text-slate-300">{r.category || "Genel Servis"} — {[r.brand, r.model].filter(Boolean).join(" ") || "Araç"} <span className="text-xs text-slate-500">• {fmtDate(r.updated_at)}</span></p>
                      <span className={`text-xs px-3 py-1 rounded-full border font-semibold ${st.cls}`}>{st.label}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Kayıtlarım */}
        <QuoteInbox />
        <div data-testid="servis-records-card" className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl overflow-hidden">
          <h2 className="text-lg font-semibold text-slate-100 px-5 sm:px-7 pt-5">Eklediğim Servis Kayıtları ({records.length})</h2>
          <div className="p-5 sm:p-7 pt-4 overflow-x-auto">
            {records.length === 0 ? (
              <p className="text-sm text-slate-500">Henüz servis kaydı eklemediniz.</p>
            ) : (
              <table data-testid="servis-records-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="py-3 pr-4">Tarih</th>
                    <th className="py-3 pr-4">Şasi No</th>
                    <th className="py-3 pr-4">İşlem</th>
                    <th className="py-3 pr-4">KM</th>
                    <th className="py-3 pr-4">Tutar</th>
                    <th className="py-3 text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.record_id} className="border-b border-white/5 hover:bg-slate-800/40 transition-colors">
                      <td className="py-3 pr-4 text-slate-400 whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td className="py-3 pr-4 font-mono-vin text-cyan-300 text-xs">{r.vin}</td>
                      <td className="py-3 pr-4 text-slate-200">{r.title}</td>
                      <td className="py-3 pr-4 text-slate-400">{fmtKm(r.km)}</td>
                      <td className="py-3 pr-4 text-slate-400">{fmtTry(r.cost) || "-"}</td>
                      <td className="py-3 text-right">
                        <button data-testid={`servis-record-delete-${r.record_id}`} onClick={() => removeRecord(r.record_id)}
                          className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-red-300 hover:border-red-400/40 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
